import axios from "axios";
import { fetchSecret } from "../../config/secret-retriever.config";

const axiosInstance = axios.create({
  baseURL: fetchSecret("PUBLIC_BASE_PATH"),
  timeout: 60000,
});

// Enable sending cookies with requests
axiosInstance.defaults.withCredentials = true;

// Helper functions for cookies
const getCookie = (name) => {
  return document.cookie.split("; ").reduce((r, v) => {
    const parts = v.split("=");
    return parts[0] === name ? decodeURIComponent(parts[1]) : r;
  }, "");
};

const setCookie = (name, value, days = 7) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(
    value
  )}; expires=${expires}; path=/`;
};

const removeCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

export const setAuthToken = (token) => {
  if (token) {
    axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete axiosInstance.defaults.headers.common["Authorization"];
  }
};

let isRefreshing = false;
let refreshPromise = null;

const refreshAndSaveToken = async (refreshToken) => {
  if (isRefreshing) {
    return refreshPromise;
  }
  isRefreshing = true;
  const apiKey = fetchSecret("FIREBASE_API_KEY");
  const url = `https://securetoken.googleapis.com/v1/token?key=${apiKey}`;

  refreshPromise = fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: `grant_type=refresh_token&refresh_token=${refreshToken}`,
  })
    .then((res) => {
      if (!res.ok) {
        throw new Error("Failed to fetch fresh token from securetoken API");
      }
      return res.json();
    })
    .then((data) => {
      isRefreshing = false;
      refreshPromise = null;
      if (data.access_token || data.id_token) {
        const nt = {
          accessToken: data.id_token || data.access_token,
          refreshToken: data.refresh_token,
          expirationTime: Date.now() + Number(data.expires_in) * 1000,
        };
        setCookie("token", JSON.stringify(nt));
        setAuthToken(nt.accessToken);
        return nt;
      }
      throw new Error("Securetoken response missing access_token / id_token");
    })
    .catch((err) => {
      isRefreshing = false;
      refreshPromise = null;
      console.error("Token refresh helper error:", err);
      return null;
    });

  return refreshPromise;
};

const handleRefreshFailure = () => {
  removeCookie("token");
  setAuthToken("");
  if (window.location.pathname !== "/login") {
    console.warn("Auth token expired and refresh failed. Redirecting to login...");
    window.location.assign("/login");
  }
};

axiosInstance.interceptors.request.use(
  async (config) => {
    if (config.data instanceof FormData) {
      config.headers["Content-Type"] = "multipart/form-data";
      config.timeout = 60000;
    } else {
      config.headers["Content-Type"] = "application/json";
    }

    // Proactively check token expiry
    const stored = getCookie("token");
    if (stored) {
      try {
        const t = JSON.parse(stored);
        if (t?.expirationTime) {
          const exp =
            typeof t.expirationTime === "string"
              ? new Date(t.expirationTime).getTime()
              : t.expirationTime;

          // If token has expired or is about to expire within 60 seconds
          if (exp - Date.now() < 60000) {
            console.log("Token expired or close to expiry, refreshing proactively...");
            if (t.refreshToken) {
              const nt = await refreshAndSaveToken(t.refreshToken);
              if (nt) {
                config.headers["Authorization"] = `Bearer ${nt.accessToken}`;
              } else {
                handleRefreshFailure();
                return Promise.reject(new Error("Auth token refresh failed"));
              }
            } else {
              handleRefreshFailure();
              return Promise.reject(new Error("No refresh token available"));
            }
          } else {
            // Token is still valid, set authorization header
            config.headers["Authorization"] = `Bearer ${t.accessToken}`;
          }
        }
      } catch (err) {
        console.error("Error checking token expiry in request interceptor:", err);
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response?.data,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      console.log("Refreshing Firebase token and retrying request...");
      try {
        const stored = getCookie("token");
        const t = stored ? JSON.parse(stored) : null;
        if (t?.refreshToken) {
          const nt = await refreshAndSaveToken(t.refreshToken);
          if (nt) {
            originalRequest.headers["Authorization"] = `Bearer ${nt.accessToken}`;
            return axiosInstance(originalRequest);
          }
        }
        handleRefreshFailure();
        return Promise.reject(error);
      } catch (refreshError) {
        console.error("Firebase token refresh failed:", refreshError);
        handleRefreshFailure();
        return Promise.reject(refreshError);
      }
    }

    console.error("Request failed:", {
      message: error.message,
      code: error.code,
      status: error.response?.status,
      data: error.response?.data,
    });
    if (!originalRequest?.skipAlert) {
      alert(
        `Error: ${
          error.response?.data?.message ||
          error.message ||
          "Unknown error occurred."
        }`
      );
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
